import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie';
import * as Sentry from '@sentry/browser';
import addWeeks from 'date-fns/addWeeks';
import {
  of as observableOf,
  Observable,
  BehaviorSubject,
  throwError,
  mergeMap,
  map,
  catchError,
  filter,
  first,
  tap,
  firstValueFrom
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { IUser } from '../models/User';
import { IntercomService } from './intercom.service';
import { ITokenObj } from '../../guest/guest-state.service';
import { IQuestionnaireToken } from '../models/QuestionnaireToken';
import { StorageService } from './storage.service';
import { PasswordRequirement } from '../gql/types';
import { SessionStatusService } from './sessionStatus.service';
import { MixpanelService } from './mixpanel.service';

const PIN_KEY = 'pin';

interface ProviderInfo {
  provider: string;
  provider_name: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  authErrorMessages: any = {
    invalid: 'Unable to login with those credentials',
    inactive: 'That user account is inactive',
    missing: 'Missing a required field.',
    locked: 'Your account has been locked from too many failed login attempts.  Try again after a few hours.',
    // test for proof of concept partial auth pipeline
    nickname: 'Nickname authentication failed'
  };

  private guestPin: string;
  private currentUserSubject: BehaviorSubject<IUser | null>;
  public currentUser$: Observable<IUser>;
  public currentUser: IUser | any;
  public capabilities: { [capability: string]: boolean };

  private serverAuthCheckFinished: Observable<boolean>;

  constructor(
    private http: HttpClient,
    private storage: StorageService,
    private cookies: CookieService,
    private router: Router,
    private intercom: IntercomService,
    private sessionStatus: SessionStatusService,
    private mixpanel: MixpanelService
  ) {
    this.guestPin = this.storage.get(PIN_KEY);
    this.currentUser = <IUser>this.storage.getObject('userData') || {};
    this.currentUserSubject = new BehaviorSubject(this.currentUser);
    this.currentUser$ = this.currentUserSubject
      .asObservable()
      .pipe(tap((user) => this._setCapabilities(user?.capabilities)));
    this.sessionStatus.watchCurrentUser(this.currentUser$);

    this.serverAuthCheckFinished = this._checkAuthWithServer();
    intercom.boot();
  }

  getAuthHeaders(): HttpHeaders {
    const headers = new HttpHeaders();
    this.guestPin = this.storage.get(PIN_KEY);

    if (this.guestPin) {
      headers.append('Authorization', `pin ${this.guestPin}`);
    }

    const csrfToken = this.cookies.get(environment.csrfCookieName);
    if (csrfToken) {
      headers.append('X-CSRFToken', csrfToken);
    }

    return headers;
  }

  /** Sets the current user in cookies and local storage */
  private _setAuth(userData: any): void {
    this.storage.putObject('userData', userData);
    const opts = {
      expires: addWeeks(new Date(), 2),
      secure: environment.secureCookie,
      domain: environment.cookieDomain
    };
    this.storage.put('userId', userData.id);

    // re-firing this observable now makes sense and helps prevent any issues with the
    // serverAuthCheckFinished observable being stale
    this.serverAuthCheckFinished = observableOf(true);
  }

  /** Sets the current pin in local storage */
  private _setGuestAuth(pin: string): void {
    this.storage.put('pin', pin);
  }

  /** Clears the current user from cookies and local storage */
  private _clearAuth(): void {
    this.storage.remove('pin');
    this.storage.remove('userData'); // No longer being used
    this.storage.remove('userId');
    this.cookies.remove('authToken');
    this.cookies.remove('csrftoken');
    this.cookies.remove('sessionid');
  }

  /** Starts intercom and sentry */
  private _bootAnalytics(user: IUser): void {
    if (!user) {
      return;
    }

    // Sentry
    if (environment.production && !environment.review) {
      Sentry.withScope((scope) => {
        scope.setUser({
          email: user.email,
          id: user.id
        });
      });
    }

    // Intercom
    this.intercom.boot({
      email: user.email,
      user_id: user.id,
      first_name: user.first_name,
      last_name: user.last_name,
      name: `${user.first_name} ${user.last_name}`,
      created_at: new Date(user.created).valueOf(),
      user_hash: user.intercom_hash,
      widget: {
        activator: '#IntercomDefaultWidget'
      }
    });

    // Mixpanel
    this.mixpanel.identify(user.id);
    this.mixpanel.track('mix_tab_authed');
    if (user?.fragomen_id) {
      this.mixpanel.peopleSetOnce({ fragomen_id: user.fragomen_id });
    }
    if (user?.email) {
      this.mixpanel.peopleSetOnce({ email: user.email });
    }
  }

  private _logoutAnalytics() {
    this.mixpanel.track('mix_logout', null, null, this.mixpanel.reset.bind(this));
  }

  /**
   * Checks with the server if the auth token is actually valid
   */
  private _checkAuthWithServer(): Observable<boolean> {
    const obs = new BehaviorSubject<null | boolean>(null);
    const headers = new HttpHeaders();
    if (this.storage.get('pin')) {
      headers.append('Authorization', `pin ${this.storage.get('pin')}`);
    }
    this.http.get<IUser>(environment.endpoints.meUser, { headers }).subscribe({
      next: (u) => {
        this._onLogin(u);
        obs.next(true);
      },
      error: (error) => {
        obs.next(false);
      }
    });
    return obs.pipe(
      filter((value) => value !== null),
      first()
    );
  }

  /**
   * onLogin hooks
   *
   * @private
   */
  private _onLogin(user: IUser): void {
    this.currentUserSubject.next(user);
    this.currentUser = user;
    this._bootAnalytics(user);
  }

  /**
   * onLogout hooks
   *
   * @private
   */
  private _onLogout(): void {
    // This function can be triggered twice on logout so let's only send
    // the analytics for the first one by checking to see if the currentUser has
    // been reset
    if (this.currentUser) {
      this._logoutAnalytics();
    }
    this.currentUserSubject.next(null);
    this.currentUser = null;
  }

  /**
   * Get a user from the current session
   *
   * @returns {Observable<IUser>}
   * @private
   */
  _userFromApi(token?: string): Observable<IUser> {
    return this.http.get(environment.endpoints.meUser).pipe(
      map((userData: any) => {
        this._setAuth(userData);
        return <IUser>userData;
      })
    );
  }

  private _setCapabilities(capabilities: string[] = []) {
    this.capabilities = capabilities.reduce(
      (allCapabilities, capability) => ({ ...allCapabilities, [capability]: true }),
      {}
    );
  }

  pinLogin(pin: string, q_response: string): Observable<any> {
    const getPin = pin ? pin : this.guestPin;

    if (!getPin) {
      return observableOf(null);
    }

    const tokenObject: ITokenObj = {
      pin: getPin,
      q_response
    };
    return this.http.post(environment.endpoints.questionnaireToken + `check_pin/`, tokenObject).pipe(
      mergeMap((res: any) => {
        if (res) {
          this._setGuestAuth(getPin);
        }
        return observableOf(<IQuestionnaireToken>res);
      }),
      map((questionnaireToken: IQuestionnaireToken) => {
        return questionnaireToken;
      }),
      catchError((err) => {
        if (err.error === 'Validation failed') {
          this.logout();
        }
        return throwError(() => err);
      })
    );
  }

  /**
   *
   * @param {string} username
   * @param {string} pin
   * @returns {Observable<any>}
   */
  inviteLogin(username: string, pin: string): Observable<any> {
    return this.http
      .post(environment.endpoints.apiTokenAuth, {
        username,
        pin
      })
      .pipe(
        mergeMap((res: any) => {
          return this._userFromApi(res.token);
        }),
        map((user: IUser) => {
          this._onLogin(user);
          return user;
        })
      );
  }

  getSocialLoginURL(email: string, nextPath: string = '%2Fbriefcase%2Fdashboard%2F&amp'): Observable<any> {
    return this.http.post<ProviderInfo>(`${environment.endpoints.checkSocial}?next=${nextPath}`, { email }).pipe(
      map((res: ProviderInfo) => {
        if (res.provider && res.provider_name) {
          return `auth/login/${res.provider}/?next=${nextPath}&idp=${res.provider_name}`;
        }

        return null;
      })
    );
  }

  /**
   * Clears auth token and user data cache
   */
  logout(redirect = true, redirectBackPath?: string): void {
    this.http.post(environment.endpoints.logout, {}).subscribe((resp) => {
      this._clearAuth();
      this._onLogout();
      this.serverAuthCheckFinished = observableOf(false);
      if (redirect) {
        this.router.navigate(['/welcome'], { queryParams: { next: redirectBackPath } });
      }
    });
    this._clearAuth();
    this._onLogout();
  }

  logoutGuest(responseId: string) {
    this.logout(false);
    this.router.navigate(['/guest', responseId, 'welcome']);
  }

  isGuestLoggedIn(): Observable<boolean> {
    if (this.storage.get('pin')) {
      return observableOf(true);
    }
    return observableOf(false);
  }

  isLoggedIn(): Observable<boolean> {
    return this.serverAuthCheckFinished;
  }

  isManager(): Observable<boolean> {
    // tslint:disable-next-line:no-console
    console.info('USER.role canary on isManager');
    return this.currentUser$.pipe(
      map((user) => {
        if (user) {
          return user.role === 'ATTORNEY' || user.role === 'PARALEGAL' || user.is_superuser;
        }
        return false;
      })
    );
  }

  isAttorney(): Observable<boolean> {
    // tslint:disable-next-line:no-console
    console.info('USER.role canary on isAttorney');
    return this.currentUser$.pipe(
      map((user) => {
        if (user) {
          return user.role === 'ATTORNEY';
        }
        return false;
      })
    );
  }

  isParalegal(): Observable<boolean> {
    // tslint:disable-next-line:no-console
    console.info('USER.role canary on isParalegal');
    return this.currentUser$.pipe(
      map((user) => {
        if (user) {
          return user.role === 'PARALEGAL';
        }
        return false;
      })
    );
  }

  isRepresentative(): Observable<boolean> {
    return this.currentUser$.pipe(
      map((user) => {
        if (user) {
          return user.is_representative || user.is_company_manager;
        }
        return false;
      })
    );
  }

  canImpersonate(): Observable<boolean> {
    return this.currentUser$.pipe(
      map((user) => {
        if (user) {
          return user.capabilities.includes('allow_impersonation');
        }
        return false;
      })
    );
  }

  forgotPassword(forgotPasswordEmail: string): Observable<any> {
    const email = forgotPasswordEmail.trim();
    return this.http.post(environment.endpoints.resetPassword + `${email}/`, {
      email
    });
  }

  setNewPassword(setPasswordText: string, validationKey: string): Observable<any> {
    return this.http.post(environment.endpoints.setNewPassword + `${validationKey}/`, {
      password: setPasswordText,
      re_enter_password: setPasswordText
    });
  }

  // TODO: Is this being used anymore?
  updateUserName(userData: any): Promise<any> {
    return firstValueFrom(
      this.http.patch(`${environment.endpoints.userApi}${this.currentUser.id}/`, {
        first_name: userData.first_name,
        last_name: userData.last_name
      })
    );
  }

  /**
   * Hotfix for navbar, so img icon updates when user updates their profile picture
   */
  setCurrentUserImage(imgUrl: string) {
    this.currentUser.image = imgUrl;
  }

  passwordRequirements(email): Observable<PasswordRequirement[]> {
    return this.http.get(`${environment.endpoints.passwordRequirement}${email}/`).pipe(
      map((requirements: any[]) =>
        requirements.map((requirement) => ({
          helpText: requirement.help_text,
          isValid: requirement.is_valid,
          validationError: requirement.validation_error
        }))
      )
    );
  }

  startImpersonation(userId) {
    if (this.canImpersonate()) {
      this.http
        .post(`${environment.endpoints.startImpersonation}`, {
          user_id: userId
        })
        .pipe(map(({ success }: any) => success))
        .subscribe(
          (success) => {
            if (success) {
              window.location.href = '/welcome';
            }
          },
          (error) => {}
        );
    }
  }

  stopImpersonation() {
    if (this.canImpersonate()) {
      this.http
        .post(`${environment.endpoints.stopImpersonation}`, {})
        .pipe(map(({ success }: any) => success))
        .subscribe({
          next: (success) => {
            if (success) {
              window.location.href = '/';
            }
          },
          error: (error) => {}
        });
    }
  }
}
